/**
 * React Query hooks for Wallet Screening
 *
 * Hooks for crypto wallet screening and transaction monitoring.
 */

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth';
import { WalletScreeningService } from '../services/walletScreening';

// Query key factory
export const walletScreeningKeys = {
  all: ['walletScreening'],
  byApplicant: (applicantId) => [...walletScreeningKeys.all, 'applicant', applicantId],
};

/**
 * Hook to get a memoized WalletScreeningService instance
 */
function useWalletScreeningService() {
  const { getToken } = useAuth();
  return useMemo(() => new WalletScreeningService(getToken), [getToken]);
}

/**
 * Hook to fetch wallet screenings for an applicant
 */
export function useWalletScreenings(applicantId, options = {}) {
  const service = useWalletScreeningService();

  return useQuery({
    queryKey: walletScreeningKeys.byApplicant(applicantId),
    queryFn: ({ signal }) => service.getByApplicant(applicantId, { signal }),
    enabled: !!applicantId,
    staleTime: 30000,
    ...options,
  });
}

/**
 * Hook to screen a wallet address
 */
export function useScreenWallet() {
  const service = useWalletScreeningService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => service.screenWallet(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: walletScreeningKeys.byApplicant(variables.applicant_id),
      });
    },
  });
}

/**
 * Hook to import wallet screening from external provider
 */
export function useImportWalletScreening() {
  const service = useWalletScreeningService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => service.importScreening(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: walletScreeningKeys.byApplicant(variables.applicant_id),
      });
    },
  });
}

/**
 * Hook to resolve a wallet screening hit
 */
export function useResolveWalletScreening() {
  const service = useWalletScreeningService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ screeningId, resolution, notes }) =>
      service.resolveScreening(screeningId, resolution, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: walletScreeningKeys.all,
      });
    },
  });
}
